import { heroData } from '@/mocks/homeData';

export default function Hero() {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-[600px] md:min-h-[680px] flex items-center overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50"></div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-primary-100/40 blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-64 h-64 md:w-80 md:h-80 rounded-full bg-accent-100/30 blur-3xl"></div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-24">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left: Text content */}
          <div className="flex-1 w-full text-center lg:text-left">
            {/* Tag */}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100/80 text-primary-700 text-xs md:text-sm font-medium mb-5">
              <i className="ri-flashlight-line text-sm"></i>
              {heroData.tag}
            </span>

            {/* H1 */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {heroData.h1}
            </h1>

            {/* Slogan */}
            <p className="text-lg md:text-xl text-primary-600 font-medium mb-6">
              {heroData.slogan}
            </p>

            {/* Intro */}
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-2xl lg:max-w-none">
              {heroData.intro}
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6 mb-8">
              {heroData.stats.map((stat, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-100"
                >
                  <span className="text-xl md:text-2xl font-heading font-bold text-primary-600">
                    {stat.value}
                  </span>
                  <span className="text-xs md:text-sm text-foreground-600 whitespace-nowrap">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href={heroData.ctaPrimary.href}
                onClick={(e) => handleClick(e, heroData.ctaPrimary.href)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white text-sm md:text-base font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                {heroData.ctaPrimary.label}
              </a>
              <a
                href={heroData.ctaSecondary.href}
                onClick={(e) => handleClick(e, heroData.ctaSecondary.href)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 border-secondary-300 text-secondary-700 text-sm md:text-base font-semibold hover:bg-secondary-50 hover:border-secondary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-bar-chart-line text-lg"></i>
                {heroData.ctaSecondary.label}
              </a>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="flex-1 w-full flex items-center justify-center">
            <div className="relative w-full max-w-md lg:max-w-lg aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20entertainment%20media%20streaming%20concept%20with%20warm%20coral%20and%20gold%20gradient%20background%2C%20abstract%20geometric%20play%20button%20and%20film%20strip%20elements%2C%20minimalist%20design%2C%20soft%20glowing%20accents%2C%20cinematic%20atmosphere%2C%20clean%20digital%20art%20style%2C%20streaming%20platform%20aesthetic&width=900&height=675&seq=hero-main-visual&orientation=landscape"
                alt="吃瓜在线视频聚合平台主视觉"
                className="w-full h-full object-cover rounded-2xl"
              />
              {/* Overlay glass card */}
              <div className="absolute bottom-4 left-4 right-4 bg-background-50/90 backdrop-blur-md rounded-xl p-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 flex-shrink-0">
                  <i className="ri-play-fill text-white text-lg"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900 truncate">
                    最新吃瓜视频正在热播
                  </p>
                  <p className="text-xs text-foreground-500">51吃瓜 · 91吃瓜 联合呈现</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}